<template>
  <div>
    <div>
      <p v-if="store.state.guestLogin.navigationSource === 'UpdateCommunication'">
        You need to have a verified email address on file before you can enroll in paperless communication. Please follow the prompts below to update / verify your email.
      </p>
      <p v-if="!store.state.guestLogin.createContact">Below is your existing contact information. If the contact information has been validated earlier with Multi Factor Authentication (MFA) it will have a green check against it.
        To update your contact information or to verify it, please check the box against the contact that you want to update / verify and click on 'Next'

        <span v-if="store.state.guestLogin.createContact">Please enter your contact information below and click on 'Next'. We will send you a login code to your new contact information.</span> <br />
        <span v-if="store.state.guestLogin.createContact">
          Login code will be valid for limited time. Enter the login code on the following screen and upon successful login code validation your contact information will be updated and you will be navigated
          to your RI DMV Online Customer profile. Your contact information will be used for future electronic Multi Factor Authentication (MFA).
        </span>
      </p>

      <div v-if="!(store.state.guestLogin.navigationSource === 'UpdateCommunication')">
        <div class="user-info">
          <span class="user-email-heading">Email</span> 
          <div class="user-info-group">
            <div class="user-info-group-contact">
              <span for="email" class="user-info-email">{{ personalInfo.personalInformation && personalInfo.personalInformation.email ? personalInfo.personalInformation.email : 'No email on file' }}</span>
              <img v-if="personalInfo.personalInformation && personalInfo.personalInformation.isEmailVerified == true" class="verify-icon" src="static/verified-icon.png" alt="Email is verified">
            </div>
            <div class="user-info-group-contact">
              <input type="checkbox" id="update/verifyEmail" v-model="updateEmail" @change="errMsg=''">
              <label for="update/verifyEmail">Update <span class="sep-class">/</span> Verify</label>
            </div>
          </div>
        </div>

        <div class="user-info">
          <div class="user-phone-header">
            <span class="user-phone-heading">Phone</span><span class="user-phone-details">(Message and data rates may apply)</span>
          </div>
          <div class="user-info-group">
            <div class="user-info-group-contact">
              <span for="phone" class="user-info-email">{{ personalInfo.personalInformation && personalInfo.personalInformation.phone ? personalInfo.personalInformation.phone : 'No phone on file' }}</span>
              <img v-if="personalInfo.personalInformation && personalInfo.personalInformation.isPhoneVerified === true" class="verify-icon" src="static/verified-icon.png" alt="Phone is verified">
            </div>
            <div class="user-info-group-contact">
              <input type="checkbox" id="update/verifyPhone" v-model="updatePhone" @change="errMsg=''">
              <label for="update/verifyPhone">Update <span class="sep-class">/</span> Verify</label>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="user-info">
          <span class="user-email-heading">Email</span> 
          <div class="user-info-group">
            <div class="user-info-group-contact">
              <span for="email" class="user-info-email">{{ personalInfo.personalInformation && personalInfo.personalInformation.email ? personalInfo.personalInformation.email : 'No email on file' }}</span>
              <img v-if="personalInfo.personalInformation && personalInfo.personalInformation.isEmailVerified == true" class="verify-icon" src="static/verified-icon.png" alt="Email is verified">
            </div>
            <div class="user-info-group-contact">
              <input type="checkbox" id="update/verify" v-model="updateEmail" @change="errMsg=''">
              <label for="update/verify">Update <span class="sep-class">/</span> Verify</label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p v-if="errMsg" class="err-msg">{{ errMsg }}</p>
    <p v-if="errMsg" class="sr-only">{{ errMsg }}</p>
    <BaseButtonGroup>
      <button type="button" @click="handleRouting('previous')">Previous</button>
      <button type="submit" class="app-btn-primary" @click="handleRouting('next')">  
        Next
      </button>
    </BaseButtonGroup>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'; 
import { useStore } from 'vuex'; 
import { useRouter } from 'vue-router'; 
import * as COMMONS from '../../store/_commons'; 
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';

// Data (Reactive State)
const updateEmail = ref(false); 
const updatePhone = ref(false); 
const errMsg = ref(''); 

// Vuex Store
const store = useStore(); 

// Vue Router 
const router = useRouter(); 

// Computed Properties 
const personalInfo = computed(() => store.getters[COMMONS.GUEST_PERSONAL_INFO]); 

const contactInfo = computed(() => store.state.guestLogin.loginUserDetails); 

// Lifecycle Hooks 
onMounted(() => { 
  window.scroll(0, 0);
  document.body.setAttribute("tabindex", "1");
  document.body.focus();
  document.body.removeAttribute("tabindex");
  document.getElementById("app").scrollTop = 0;
});

// Methods 
const getUpdateContact = (data) => {
  store.dispatch(COMMONS.GUEST_UPDATE_CONTACT, data);
};

const handleRouting = async (info) => { 
  if (info === 'previous') {
    if (contactInfo.value && contactInfo.value.isPaperOtpSent) { 
      router.push({ path: '/guest-signin' }); 
    } else {
      router.push({ path: '/profile' });
    }
  } else if (info === 'next') { 
    if (updateEmail.value || updatePhone.value) { 
      const data = {
        email: updateEmail.value, 
        phone: updatePhone.value
      };
      getUpdateContact(data);
      errMsg.value = ''; 
    } else {
      errMsg.value = 'Please select the contact information to update/verify'; 
    }
  }
};

onUnmounted(() => {
    // Optional: Only clear if you want the state to reset every time they leave the page
    // store.state.guestLogin.navigationSource = null;
});
</script>


<style lang="scss" scoped>
.user-info {
  margin-top: 2rem;
  h3 {
    color: #000;
  }
  .user-phone-header{
    display: flex;
    align-items: baseline;
    .user-phone-heading{
      font-weight: bold;
    }
    .user-phone-details{
      margin-left: 5px;
    }
  }
  .user-email-heading{
    font-weight: bold;
  }
  .user-info-group {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 3rem;
    .user-info-group-contact {
      width: 300px;
      padding: 20px 0;
      display: flex;
      .verify-icon {
        width: 50px;
        height: 20px;
        vertical-align: middle;
        margin-left: 5px;
      }
      .user-info-email{
        width: 90%;
        word-wrap: break-word;
        font-weight: bold;
      }
      .sep-class{
        margin: 0 3px 0 2px;
      }
    }

    @media (min-width: 355px) and (max-width: 700px) {
      margin-bottom: 15px;
      .user-info-group-contact {
        padding: 5px 0;
      }
    }
  }

  @media (min-width: 355px) and (max-width: 740px) {
    .user-phone-header{
      display: block;
      .user-phone-details{
        margin-left: 0px !important;
      }
    }
    .user-info-group {
      gap: 0rem;
    } 
  }
}
.err-msg{
  color: var(--ri-red);
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>